/**
 * Thin mock API client. Every service goes through `request()` so that
 * swapping to a real backend later is a one-file change: replace the body
 * of `request` with a `fetch`/`axios` call to `import.meta.env.VITE_API_URL`.
 */
import type { ServiceResponse } from '../../types';

export const API_BASE = import.meta.env.VITE_API_URL ?? '/api';

/** Simulated network latency (ms). */
const LATENCY = [250, 650] as const;

function randomLatency() {
  return LATENCY[0] + Math.random() * (LATENCY[1] - LATENCY[0]);
}

export interface RequestOptions {
  /** Force an error to exercise error states. */
  failRate?: number;
  /** Override latency. */
  delay?: number;
}

/**
 * Resolve a mocked response after a simulated round-trip.
 * @param _endpoint kept for parity with a real client (logging / routing)
 */
export async function request<T>(
  _endpoint: string,
  resolver: () => T,
  options: RequestOptions = {},
): Promise<ServiceResponse<T>> {
  const delay = options.delay ?? randomLatency();
  await new Promise((r) => setTimeout(r, delay));

  if (options.failRate && Math.random() < options.failRate) {
    return { ok: false, data: undefined as unknown as T, message: 'Network request failed. Please retry.' };
  }

  return { ok: true, data: resolver() };
}

/** Stable mock id generator. */
export function makeId(prefix: string): string {
  return `${prefix}-${Math.random().toString(36).slice(2, 9)}`;
}
